import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import LeadAPI from 'dashboard/api/leads';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';

const camelizeLead = data => camelcaseKeys(data || {}, { deep: true });

export const LEAD_STAGES = [
  'new_lead',
  'contacted',
  'qualified',
  'proposal',
  'won',
  'lost',
];

export const LEAD_SOURCES = [
  'Website',
  'Referral',
  'Ad Campaign',
  'Cold Outreach',
  'Other',
];

export const useLeadsStore = createStore({
  name: 'leads',
  type: 'pinia',
  API: LeadAPI,
  state: () => ({
    activeLeadId: null,
    leadReminders: [],
  }),

  getters: {
    getLeadsList: state => state.records,
    getLeadsByStage: state => stage =>
      state.records.filter(lead => lead.stage === stage),
  },

  actions: () => ({
    setActiveLeadId(leadId) {
      this.activeLeadId = Number(leadId);
    },

    upsertLeadRecord(record) {
      const index = this.records.findIndex(r => r.id === record.id);
      if (index === -1) this.records.push(record);
      else this.records[index] = record;
    },

    async get(params = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const {
          data: { payload },
        } = await LeadAPI.get(params);
        this.records = camelizeLead(payload);
        return this.records;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async show(id) {
      this.setUIFlag({ fetchingItem: true });
      this.setActiveLeadId(id);
      try {
        const {
          data: { payload },
        } = await LeadAPI.show(id);
        const lead = camelizeLead(payload);
        this.upsertLeadRecord(lead);
        return lead;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingItem: false });
      }
    },

    async update({ id, ...leadAttrs }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const {
          data: { payload },
        } = await LeadAPI.update(id, {
          lead: snakecaseKeys(leadAttrs, { deep: true }),
        });
        const lead = camelizeLead(payload);
        this.upsertLeadRecord(lead);
        return lead;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async getReminders(leadId) {
      this.setUIFlag({ fetchingReminders: true });
      try {
        const {
          data: { payload },
        } = await LeadAPI.listReminders(leadId);
        this.leadReminders = camelizeLead(payload);
        return this.leadReminders;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingReminders: false });
      }
    },

    async createReminder(leadId, reminderAttrs) {
      this.setUIFlag({ creatingReminder: true });
      try {
        const {
          data: { payload },
        } = await LeadAPI.createReminder(
          leadId,
          snakecaseKeys(reminderAttrs, { deep: true })
        );
        const reminder = camelizeLead(payload);
        this.leadReminders.push(reminder);
        return reminder;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingReminder: false });
      }
    },

    async updateReminder(leadId, reminderId, reminderAttrs) {
      this.setUIFlag({ updatingReminder: true });
      try {
        const {
          data: { payload },
        } = await LeadAPI.updateReminder(
          leadId,
          reminderId,
          snakecaseKeys(reminderAttrs, { deep: true })
        );
        const reminder = camelizeLead(payload);
        const index = this.leadReminders.findIndex(
          r => r.id === reminder.id
        );
        if (index !== -1) this.leadReminders[index] = reminder;
        return reminder;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingReminder: false });
      }
    },

    async deleteReminder(leadId, reminderId) {
      this.setUIFlag({ deletingReminder: true });
      try {
        await LeadAPI.deleteReminder(leadId, reminderId);
        this.leadReminders = this.leadReminders.filter(
          r => r.id !== Number(reminderId)
        );
        return Number(reminderId);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingReminder: false });
      }
    },
  }),
});
